// vim: set ts=2 sts=2 sw=2 et:

import React from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import type { GlobalState } from "../../../types/stateTypes";
import { LAB_SCENARIOS, LAB_TOURNAMENT } from "../labScenarios";
import { LAB_SPONSORS } from "../labSponsors";
import LabStage, { parseLabZoom, useLabBody } from "../LabStage";
import AthleteCardView from "../../streaming/broadcast/AthleteCardView";
import { buildFichaView } from "../../streaming/broadcast/fichaView";
import type { PhotoFrameVariant } from "../../streaming/broadcast/PhotoFrame";
import { SAMPLE_PHOTO_URL, simulateStoredPhoto, STORED_PHOTO_SIZE } from "./simulateStoredPhoto";

import styles from "../Lab.module.scss";

// La ficha avanza apenas se resuelve el intento (immediate-next): las escenas de veredicto no le corresponden.
const FICHA_SCENARIOS = LAB_SCENARIOS.filter((scenario) => scenario.reveal === null);

const FRAMES: ReadonlyArray<{ id: PhotoFrameVariant; label: string }> = [
  { id: "medalla", label: "Medalla" },
  { id: "recuadro", label: "Recuadro" },
];

const SIZES = [360, 420, 480];

type Quality = "hoy" | "original";

const QUALITIES: ReadonlyArray<{ id: Quality; label: string }> = [
  { id: "hoy", label: `Como hoy (${STORED_PHOTO_SIZE}×${STORED_PHOTO_SIZE} comprimida)` },
  { id: "original", label: "Original (como tras la mejora)" },
];

const pick = <T extends string>(value: string | null, options: ReadonlyArray<{ id: T }>, fallback: T): T =>
  options.some((option) => option.id === value) ? (value as T) : fallback;

const FichaLabRoute = () => {
  useLabBody();
  const location = useLocation();
  const initial = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const meetName = useSelector((state: GlobalState) => state.meet.name.trim());

  const [scenarioId, setScenarioId] = React.useState(() => pick(initial.get("escena"), FICHA_SCENARIOS, FICHA_SCENARIOS[0].id));
  const [frame, setFrame] = React.useState<PhotoFrameVariant>(() => pick(initial.get("marco"), FRAMES, "medalla"));
  const [size, setSize] = React.useState(() => {
    const requested = Number(initial.get("tamano"));
    return SIZES.includes(requested) ? requested : 420;
  });
  const [quality, setQuality] = React.useState<Quality>(() => pick(initial.get("calidad"), QUALITIES, "hoy"));
  const [controlsVisible, setControlsVisible] = React.useState(initial.get("controles") !== "0");
  // Arranca con una silueta de prueba para ver el marco sin subir nada; la foto propia queda solo en el navegador.
  const [sourceUrl, setSourceUrl] = React.useState<string | null>(SAMPLE_PHOTO_URL);
  const [storedUrl, setStoredUrl] = React.useState<string | null>(null);

  const scenario = FICHA_SCENARIOS.find((candidate) => candidate.id === scenarioId) ?? FICHA_SCENARIOS[0];

  const step = React.useCallback((delta: number) => {
    setScenarioId((current) => {
      const index = FICHA_SCENARIOS.findIndex((candidate) => candidate.id === current);
      return FICHA_SCENARIOS[(index + delta + FICHA_SCENARIOS.length) % FICHA_SCENARIOS.length].id;
    });
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams({ escena: scenarioId, marco: frame, tamano: String(size), calidad: quality });
    if (!controlsVisible) params.set("controles", "0");
    const zoom = initial.get("zoom");
    if (zoom !== null) params.set("zoom", zoom);
    window.history.replaceState(null, "", `${location.pathname}?${params.toString()}`);
  }, [controlsVisible, frame, initial, location.pathname, quality, scenarioId, size]);

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLSelectElement || event.target instanceof HTMLInputElement) return;
      if (event.key === "h" || event.key === "H") setControlsVisible((visible) => !visible);
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  // Versión "como hoy" de la foto elegida, procesada igual que la web.
  React.useEffect(() => {
    if (sourceUrl === null) {
      setStoredUrl(null);
      return undefined;
    }
    let cancelled = false;
    let created: string | null = null;
    simulateStoredPhoto(sourceUrl)
      .then((url) => {
        created = url;
        if (!cancelled) setStoredUrl(url);
      })
      .catch(() => {
        if (!cancelled) setStoredUrl(null);
      });
    return () => {
      cancelled = true;
      if (created !== null) URL.revokeObjectURL(created);
    };
  }, [sourceUrl]);

  React.useEffect(
    () => () => {
      if (sourceUrl !== null && sourceUrl.startsWith("blob:")) URL.revokeObjectURL(sourceUrl);
    },
    [sourceUrl],
  );

  const onPhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file !== undefined) setSourceUrl(URL.createObjectURL(file));
  };

  const photoUrl = sourceUrl === null ? null : quality === "hoy" ? storedUrl ?? sourceUrl : sourceUrl;
  const baseView = scenario.attempt === null ? null : buildFichaView(scenario.attempt);
  // La foto elegida reemplaza la de todos los atletas de ejemplo: el lab no guarda fotos reales.
  const athlete = baseView === null ? null : { ...baseView, photoUrl };

  return (
    <>
      <LabStage backdrop="blanco" imageUrl={null} zoom={parseLabZoom(initial.get("zoom"))}>
        <AthleteCardView
          tournamentName={meetName || LAB_TOURNAMENT.name}
          sessionLabel={`Día ${LAB_TOURNAMENT.day}, tarima ${LAB_TOURNAMENT.platformId}`}
          sponsors={LAB_SPONSORS}
          athlete={athlete}
          waitingForNext={scenario.waitingForNext === true}
          photoFrame={frame}
          photoSize={size}
        />
      </LabStage>

      {controlsVisible ? (
        <aside className={styles.controls} aria-label="Controles del lab">
          <div className={styles.controlsTitle}>
            <h1>Ficha del atleta</h1>
            <Link to="/lab">Lab</Link>
          </div>
          <label>
            Escena
            <select value={scenario.id} onChange={(event) => setScenarioId(event.target.value)}>
              {FICHA_SCENARIOS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.controlsRow}>
            <label>
              Marco
              <select value={frame} onChange={(event) => setFrame(event.target.value as PhotoFrameVariant)}>
                {FRAMES.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Tamaño
              <select value={size} onChange={(event) => setSize(Number(event.target.value))}>
                {SIZES.map((option) => (
                  <option key={option} value={option}>
                    {option}px
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Calidad de la foto
            <select value={quality} onChange={(event) => setQuality(event.target.value as Quality)}>
              {QUALITIES.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Probar con una foto
            <input type="file" accept="image/*" onChange={onPhoto} />
          </label>
          <div className={styles.controlsRow}>
            <button type="button" onClick={() => setSourceUrl(SAMPLE_PHOTO_URL)}>
              Silueta
            </button>
            <button type="button" onClick={() => setSourceUrl(null)} disabled={sourceUrl === null}>
              Sin foto
            </button>
          </div>
          <div className={styles.controlsRow}>
            <button type="button" onClick={() => step(-1)}>
              Anterior
            </button>
            <button type="button" onClick={() => step(1)}>
              Siguiente
            </button>
          </div>
          <p className={styles.controlsHint}>
            &quot;Como hoy&quot; procesa la foto igual que la web: {STORED_PHOTO_SIZE}×{STORED_PHOTO_SIZE}, JPEG 0,9 y
            WebP 0,8. H oculta los controles y ← → cambian de escena.
          </p>
        </aside>
      ) : null}
    </>
  );
};

export default FichaLabRoute;
